import { Outlet, NavLink } from 'react-router-dom'
import { Inbox, FlaskConical, Settings as SettingsIcon, Database, LogOut } from 'lucide-react'
import { useAuth } from '../auth'
import ActAsPicker from './ActAsPicker'
import TweaksPanel from './TweaksPanel'

// Top-level tabs are intentionally minimal:
//   - Workspace + Settings: end-user / attorney facing
//   - Imports + Studio: admin-only (admin tooling lives under Studio)
// Pipeline / Prompts / Observability / Artifacts moved under Studio
// (they were already accessible there; the top-level duplicates were
// removed to consolidate the operator surface in one place).
const tabs = [
  { to: '/workspace', label: 'Workspace', icon: Inbox },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
  { to: '/imports/v1', label: 'Imports', icon: Database, adminOnly: true },
  { to: '/studio', label: 'Studio', icon: FlaskConical, adminOnly: true },
]

export default function Layout() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null

  return (
    <div className="flex h-screen flex-col bg-[color:var(--me-bg)]">
      <header className="flex items-center gap-1 border-b me-border bg-[color:var(--me-surface)] px-3 py-2">
        <div className="me-mono mr-3 flex items-center gap-2 text-[13px] font-semibold tracking-tight">
          <span className="inline-block h-3 w-3 rounded-sm bg-[color:var(--me-fg)]" />
          Matters
        </div>
        <nav className="flex flex-1 gap-0.5">
          {tabs.filter((t) => !t.adminOnly || (user && user.has_studio_access)).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to}>
              {({ isActive }) => (
                <span data-selected={isActive ? 'true' : undefined} className="me-tab inline-flex items-center gap-1.5">
                  <Icon size={14} strokeWidth={1.6} />
                  {label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 text-[12px] me-text-mute">
          {user && (
            <ActAsPicker user={user} onChange={() => auth.refresh()} />
          )}
          {user && (
            <span
              className="me-mono"
              title={user.acting_as
                ? `Logged in as ${user.acting_as.real_username} (superuser); acting as ${user.username}`
                : user.username}
            >
              {user.username}
              {user.client && (
                <span className="me-text-mute"> · {user.client.code}</span>
              )}
            </span>
          )}
          <TweaksPanel />
          <button onClick={() => auth.logout()} className="me-btn me-btn-outline" title="Log out">
            <LogOut size={13} />
          </button>
        </div>
      </header>
      <main className="me-scroll flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
